import React, { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';

import BasketItemTable from './BasketItemTable';
import BasketItemRingChart from './BasketItemRingChart';
import SearchBar from '../../../../../../shared/designSystem/components/SearchBar';
import EntityBreakdownCard from '../../../../../../shared/designSystem/components/EntityBreakdownCard';
import { MenuItemText } from '../../../../../../shared/globalStyling/styledText';
import { getRingColor } from '../../../../../../shared/designSystem/components/BreakdownRingChart';

const { Option } = Select;

const SHOW_BY_OPTIONS = [
  { value: 'quantity', text: 'Quantity' },
  { value: 'total', text: 'Total' },
];

// =============================================================================
// BasketItemDetails — unified Basket Items section card (V2 tab).
//
// Same shape as every other entity section on the Campaign Details tabs:
// `.campaign-overview__creatives` wrapper, head eyebrow + tag-search in the
// main head, table in the body, breakdown donut in the aside. Dimension
// selector lives in the breakdown head.
//
// Search input is a tag-search (AntD Select mode="tags") so users can add
// multiple item-name fragments; parent handles the filtering.
// =============================================================================

const BasketItemDetails = ({
  basketItems = [],
  searchTerms = [],
  onSearchChange,
}) => {
  const [basketItemDimension, setBasketItemDimension] = useState('total');

  // Local string state drives the input; parent's `searchTerms` stays a
  // string array for back-compat with the controller's filter logic — the
  // SearchBar's debounced onSearch callback wraps the single value in a
  // length-1 array. SearchBar owns the 300ms debounce.
  const [searchValue, setSearchValue] = useState(
    (searchTerms && searchTerms[0]) || ''
  );

  useEffect(() => {
    // Keep local value in sync if parent clears / replaces the terms
    // externally (e.g., other filters reset the search).
    const next = (searchTerms && searchTerms[0]) || '';
    setSearchValue(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchTerms || [])]);

  const itemsWithColors = useMemo(() => {
    // Sort by total desc (preserves v1 behavior) and attach stable slice
    // colors from the shared palette.
    return [...basketItems]
      .sort((a, b) => (b.total || 0) - (a.total || 0))
      .map((item, index) => ({
        ...item,
        color: getRingColor(index),
        value: item.name || `basket-item-${index}`,
      }));
  }, [basketItems]);

  return (
    <EntityBreakdownCard
      title="Basket Items"
      search={
        <SearchBar
          placeholder="Search basket items..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={(v) => onSearchChange && onSearchChange(v ? [v] : [])}
          className="campaign-overview__creatives-search"
        />
      }
      table={<BasketItemTable basketItemData={itemsWithColors} />}
      breakdownControl={
        <Select
          value={basketItemDimension}
          onChange={(value) => setBasketItemDimension(value)}
          getPopupContainer={(trigger) => trigger.parentNode}
          popupClassName="mjl-ds"
          className="campaign-overview__creatives-showby"
          size="small"
        >
          {SHOW_BY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              <MenuItemText text={option.text} />
            </Option>
          ))}
        </Select>
      }
      breakdownChart={
        <BasketItemRingChart
          data={itemsWithColors}
          height={480}
          selectedBasketItemDimension={basketItemDimension}
        />
      }
    />
  );
};

export default BasketItemDetails;
